import {
  createSlice,
  createAsyncThunk,
  createSelector } from
'@reduxjs/toolkit';
import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client';
import { RootState } from '@src/redux/store';
import * as TYPES from '@src/redux/types';

const GRAPHQL = gql`
  query getSubjectDoctorLogList(
    $filter: subjectDoctorLogFilterInput
    $pagination: paginationInput
    $sort: [sortInput]
  ) {
    getSubjectDoctorLogList(
      filter: $filter
      pagination: $pagination
      sort: $sort
    ) {
      items {
        id
        subjectId
        doctorId
        status
        actionById
        actionDatetime
        doctor {
          id
          name
          code
        }
        actionBy {
          id
          name
          code
        }
      }
      pagination {
        pageNumber
        pageLimit
        totalCount
      }
      error {
        message
        statusCode
      }
    }
  }
`;

const initialState: TYPES.SubjectDoctorLogListState = {
  result: null,
  error: null,
  loading: false
};

export const getSubjectDoctorLogListRequest = createAsyncThunk<
  TYPES.SubjectDoctorLogList,
  {
    filter?: TYPES.SubjectDoctorLogFilterInput;
    pagination?: TYPES.PaginationInput;
    sort?: TYPES.SortInput[];
  },
  {extra: ApolloClient<NormalizedCacheObject>;}>(

  'subject_doctor_log/get_subject_doctor_log_list',
  async (
  params: {
    filter?: TYPES.SubjectDoctorLogFilterInput;
    pagination?: TYPES.PaginationInput;
    sort?: TYPES.SortInput[];
  },
  { extra: client }) =>
  {
    try {
      const response = await client.query({
        query: GRAPHQL,
        variables: params
      });
      return response.data.getSubjectDoctorLogList;
    } catch (error) {
      const err = error as {message?: String;statusCode?: String;};
      return Promise.reject({
        message: err.message || 'An error occurred',
        statusCode: err.statusCode || '500'
      });
    }
  }
);

const getSubjectDoctorLogListSlice = createSlice({
  name: 'get_subject_doctor_log_list',
  initialState: initialState,
  reducers: {
    getSubjectDoctorLogListClear: (state) => {
      state.result = null;
      state.error = null;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder.
    addCase(getSubjectDoctorLogListRequest.pending, (state) => {
      state.result = null;
      state.error = null;
      state.loading = true;
    }).
    addCase(
      getSubjectDoctorLogListRequest.fulfilled,
      (state, action) => {
        state.result = action.payload;
        state.loading = false;
      }
    ).
    addCase(
      getSubjectDoctorLogListRequest.rejected,
      (state, action) => {
        state.error = action.payload as Error || {
          message: 'Failed to fetch',
          statusCode: '500'
        };
        state.loading = false;
      }
    );
  }
});

export const { getSubjectDoctorLogListClear } =
getSubjectDoctorLogListSlice.actions;

export const getSubjectDoctorLogList = createSelector(
  (state: RootState) =>
  state.subjectDoctorLog.getSubjectDoctorLogList.result,
  (state: RootState) =>
  state.subjectDoctorLog.getSubjectDoctorLogList.error,
  (state: RootState) =>
  state.subjectDoctorLog.getSubjectDoctorLogList.loading,
  (result, error, loading) => ({
    result,
    error,
    loading
  })
);

export default getSubjectDoctorLogListSlice.reducer;